import { AgentMessageItem } from './AgentMessageItem'
import { LeadMessageItem } from './LeadMessageItem'
import { CallAgentItem } from './CallAgentItem'
import { CallSummaryAgentItem } from './CallSummaryAgentItem'
import { AppointmentScheduledItem } from './AppointmentScheduledItem'
import { AppointmentMissedItem } from './AppointmentMissedItem'
import { AppointmentRescheduledItem } from './AppointmentRescheduledItem'
import { FC, useRef, useEffect, useState, useMemo } from 'react'
import { StageChangeItem } from './StageChangeItem'
import { message } from 'antd'
import { EmailSentItem } from './EmailSentItem'
import { CallLeadItem } from './CallLeadItem'
import { LeadDetailData } from '@forwardflow/services/LeadService'
import { VoiceMissedItem } from './VoiceMissedItem'
import { NotesItem } from './NotesItem'
import { InitialLeadSummaryItem } from './InitialSummaryItem'
import { EventItem } from './EventItem'
import { CallSummaryLeadItem } from './CallSummaryLeadItem'
import { useDemoConversationStore } from '@forwardflow/store/demoConversationStore'
import { AgentLoaderItem } from './AgentLoaderItem'
import { MessageInput } from './MessageInput'
import { useAuth } from '@forwardflow/context/AuthContext'
import { DemoConversationService } from '@forwardflow/services/DemoConversation'
import { TimelineObjectFactory } from '@forwardflow/pages/DemoConversations/timelineObjectFactory'
import { Button } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { CtrlClickSaveWrapper } from './saveTimeLineShortcut'
import { mapCallReportToTimelineItem } from '@forwardflow/pages/DemoConversations/callObjectMapper'
import { useDemoCallSSE } from '@forwardflow/hooks/customHooks/demoConversation/useDemoCallSSE'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import useCampaigns from '@forwardflow/hooks/customHooks/demoConversation/useCampaignStepByType'
import { useDemoLoaderStore } from '@forwardflow/store/useDemoLoaderStore'
import { useDemoCampaignStore } from '@forwardflow/store/demoCampaignStepStore'
import { DemoLead } from '@forwardflow/types/demo-conversation'
import { useDemoConversationSSE } from '@forwardflow/hooks/customHooks/conversation/useDemoConversationSSE'
import { useOperatorStore } from '@forwardflow/store/useOperatorStore'

interface DemoConversationProps {
   selectedLeadDetails: LeadDetailData | undefined | null
   isDemo?: boolean
}

function getDelayShortActionFromTimeline(timeline: any[]) {
   if (timeline.length === 0) return null
   const lastItem = timeline[timeline.length - 1]
   return lastItem?.delayShortAction
}

function findLastItem<T>(
   array: T[],
   predicate: (item: T) => boolean
): T | undefined {
   return array.slice().reverse().find(predicate)
}

const DemoConversation: FC<DemoConversationProps> = ({
   selectedLeadDetails,
}) => {
   const currentLeadId = selectedLeadDetails?.userInfo?.id
   const [navigationMode, setNavigationMode] = useState<
      'campaign' | 'predefined'
   >('predefined')
   const [isSavingLocal, setIsSavingLocal] = useState(false)
   const scrollRef = useRef<HTMLDivElement>(null)
   const [optimisticMessages, setOptimisticMessages] = useState<{
      [leadId: string]: any[]
   }>({})
   const { showOperatorSection } = useOperatorStore()

   const {
      auth: { user },
   } = useAuth()
   const { data: progressDetails } = useSetupProgress()

   const {
      updateTimelineHistory,
      setLastUpdate,
      setRecentConversation,
      initializeTimelineHistory,
      incrementMessagesSent,
      incrementMessagesReplied,
      incrementCallsAttempted,
      incrementCallsAnswered,
      updateStage,
      setLeadDetails,
      updateTimelineSummary,
      updateQuestions,
      saveTimelineHistory,
      updateAppointmentStatus,
      getNextStepInfo,
      setInitialValues,
      getInitialValues,
   } = useDemoConversationStore()

   const {
      setDemoLoading,
      setNextConversationLoading,
      setWaitingForCallReport,
      getDemoLoading,
      getNextConversationLoading,
      clearAllLoadingStates,
   } = useDemoLoaderStore()

   const {
      setCampaignData,
      getCampaignSteps: getStepsFromStore,
      getNextStep,
      startActiveConversation,
      endActiveConversation,
      isActiveConversation,
      getActiveConversationInfo,
   } = useDemoCampaignStore()

   const { data: demoConversationSSEData } = useDemoConversationSSE()

   useEffect(() => {
      if (demoConversationSSEData) {
         const eventItem = TimelineObjectFactory.createEventItem({
            content: demoConversationSSEData.content ?? '',
            outcome: demoConversationSSEData.outcome ?? '',
            ...getCampaignInfo(currentLeadId ?? 0, currentStepIndex),
            step:
               (getCampaignInfo(currentLeadId ?? 0, currentStepIndex)?.step ??
                  0) - 1,
         })
         const combinedTimelineHistory = currentLeadId
            ? useDemoConversationStore
                 .getState()
                 .getTimelineHistory(demoConversationSSEData?.personId)
            : []

         const updatedTimeline = [...combinedTimelineHistory, eventItem]
         updateTimelineState(
            demoConversationSSEData?.personId ?? 0,
            updatedTimeline,
            eventItem
         )
      }
   }, [demoConversationSSEData])

   const isDemoLoading = currentLeadId ? getDemoLoading(currentLeadId) : false
   const isNextConversationLoading = currentLeadId
      ? getNextConversationLoading(currentLeadId)
      : false

   const leadConversationData = useDemoConversationStore((state) =>
      currentLeadId ? state.getLeadConversationData(currentLeadId) : null
   )

   const combinedTimelineHistory = useDemoConversationStore((state) =>
      currentLeadId ? state.getTimelineHistory(currentLeadId) : []
   )

   const preDefinedTimelineHistory = useDemoConversationStore((state) =>
      currentLeadId ? state.getPreDefinedTimelineHistory(currentLeadId) : []
   )

   const getLeadDetails = useDemoConversationStore(
      (state) => state.getLeadDetails
   )

   const { endOfCallReport, startSSEListener, isWaitingForReport } =
      useDemoCallSSE()

   const currentCampaignType = leadConversationData?.currentCampaignType
   const currentCampaign =
      leadConversationData?.currentCampaign ||
      selectedLeadDetails?.stageInfo?.name
   const currentStepIndex = leadConversationData?.timelineHistory?.length || 0
   const currentTimeline = leadConversationData?.timelineHistory || []

   const { data: campaignData } = useCampaigns(
      (currentCampaignType ?? '').toLowerCase()
   )

   const hasInitialComponents = combinedTimelineHistory.some(
      (item) =>
         item.itemType === 'init_summary' || item.itemType === 'data_validation'
   )

   const isInActiveConversation = currentLeadId
      ? isActiveConversation(currentLeadId)
      : false
   const activeConversationInfo = currentLeadId
      ? getActiveConversationInfo(currentLeadId)
      : null

   const setLoadingStates = (leadId: number, loading: boolean) => {
      setDemoLoading(leadId, loading)
      setNextConversationLoading(leadId, loading)
   }

   const updateTimelineState = (
      leadId: number,
      timeline: any[],
      lastItem?: any
   ) => {
      updateTimelineHistory(leadId, timeline)
      setRecentConversation(leadId, lastItem?.content || '', '')
      setLastUpdate(leadId, new Date().toISOString())
   }

   const getNextStepInfoMemo = useMemo(() => {
      return currentLeadId ? getNextStepInfo(currentLeadId) : null
   }, [currentLeadId, getNextStepInfo, currentTimeline.length])

   useEffect(() => {
      if (campaignData && currentCampaignType) {
         setCampaignData(currentCampaignType.toLowerCase(), {
            ...campaignData,
            type: campaignData.type ?? '',
         })
      }
   }, [campaignData, currentCampaignType, setCampaignData])

   const campaignTypeForSteps = hasInitialComponents && currentCampaignType
   const campaignSteps = getStepsFromStore(
      campaignTypeForSteps || '',
      currentCampaign || ''
   )

   const canSaveTimeline =
      currentLeadId &&
      combinedTimelineHistory.length >= 0 &&
      !isDemoLoading &&
      !isNextConversationLoading &&
      !isWaitingForReport

   const getCampaignInfo = (leadId: number, currentStepIndex: number) => {
      if (isInActiveConversation && activeConversationInfo) {
         return {
            campaignType: activeConversationInfo.lastCampaignType,
            campaignPhase: activeConversationInfo.lastCampaignPhase,
            step: activeConversationInfo.lastStep,
            stepIndex: currentStepIndex,
            isActiveConversationItem: true,
         }
      }
      const currentTimeline = useDemoConversationStore
         .getState()
         .getTimelineHistory(leadId)
      const lastCurrentTimelineDelayShortAction =
         getDelayShortActionFromTimeline(currentTimeline)
      if (lastCurrentTimelineDelayShortAction) {
         return {
            campaignType: currentCampaignType,
            campaignPhase: currentCampaign,
            step: (getNextStepInfo(leadId)?.step ?? 1) - 1,
            stepIndex: currentStepIndex,
            isActiveConversationItem: false,
         }
      }

      return {
         campaignType: currentCampaignType,
         campaignPhase: currentCampaign,
         step: getNextStepInfo(leadId)?.step ?? 1,
         stepIndex: currentStepIndex,
         isActiveConversationItem: false,
      }
   }

   const createNoteTimeline = async (
      leadId: number,
      content: string,
      additionalProps: any = {}
   ) => {
      const storeState = useDemoConversationStore.getState()
      const leadData = storeState.getLeadConversationData(leadId)
      const currentTimeline = storeState.getTimelineHistory(leadId)

      const baseProps = {
         content,
         campaignType: leadData?.currentCampaignType,
         campaignPhase: leadData?.currentCampaign,
         step: getCampaignInfo(leadId, currentTimeline.length).step - 1,
         stepIndex: currentTimeline.length,
         ...additionalProps,
      }

      const noteItem = TimelineObjectFactory.createNote(baseProps)
      const updatedTimeline = [...currentTimeline, noteItem]
      updateTimelineState(leadId, updatedTimeline, noteItem)

      return noteItem
   }

   const createStageChangeTimeline = async (
      leadId: number,
      newStage: string,
      itemType: 'appointment' | 'post_analysis',
      additionalProps: any = {}
   ) => {
      updateStage(leadId, 0, newStage)

      const storeState = useDemoConversationStore.getState()
      const { setCurrentCampaign } = storeState
      setCurrentCampaign(leadId, newStage)

      const leadData = storeState.getLeadConversationData(leadId)
      const currentTimeline = storeState.getTimelineHistory(leadId)

      const baseProps = {
         campaignType: leadData?.currentCampaignType,
         campaignPhase: newStage,
         step: 0,
         stepIndex: currentTimeline.length,
         ...additionalProps,
      }

      const timelineItem =
         itemType === 'appointment'
            ? TimelineObjectFactory.createAppointment(baseProps)
            : TimelineObjectFactory.createStage(baseProps)

      const updatedTimeline = [...currentTimeline, timelineItem]
      updateTimelineState(leadId, updatedTimeline, timelineItem)

      return timelineItem
   }

   const handleConversationOutcome = (outcome: any, leadId: number) => {
      if (!leadId) return

      if (outcome.timelineSummary) {
         updateTimelineSummary(leadId, outcome.timelineSummary)
      }

      if (outcome.questions) {
         updateQuestions(leadId, outcome.questions)
      }

      switch (outcome.itemType) {
         case 'text':
            if (outcome.isIncoming) {
               incrementMessagesReplied(leadId)
            } else {
               incrementMessagesSent(leadId)
            }
            break

         case 'call':
            incrementCallsAttempted(leadId)
            if (outcome.itemActionStatus === 'ended') {
               incrementCallsAnswered(leadId)
            }
            break

         case 'voicemail':
            incrementCallsAttempted(leadId)
            break

         case 'appointment':
            if (outcome.appointment) {
               updateAppointmentStatus(
                  leadId,
                  outcome.appointment.status || 'Scheduled',
                  outcome.appointment.date,
                  outcome.appointment.platform || 'Other'
               )
            }
            break

         default:
            break
      }
   }

   const handleSMSWithAppointment = async (
      leadDetails: DemoLead,
      response: any,
      campaignInfo: any,
      currentLeadId: number
   ) => {
      const smsItem = TimelineObjectFactory.createSMS({
         content: response.content,
         ...campaignInfo,
      })

      if (
         response.toolResult?.toolName === 'set_appointment' &&
         response.toolResult?.args
      ) {
         if (currentLeadId) {
            const timelineWithSMS = [...currentTimeline, smsItem]
            updateTimelineState(currentLeadId, timelineWithSMS, smsItem)
            handleConversationOutcome(smsItem, currentLeadId)
         }

         const appointmentItem = await createStageChangeTimeline(
            leadDetails?.userInfo?.id || 0,
            'ff-appointment-set',
            'appointment',
            {
               content: response.content || '',
               appointment: {
                  date: response.toolResult?.args?.date,
                  status: response.toolResult?.args?.status || 'Scheduled',
                  platform: response.toolResult?.args?.platform || 'Other',
               },
               timelineSummary:
                  response.toolResult?.args?.timelineSummary ||
                  `Appointment scheduled via SMS for ${response.toolResult?.args?.date}`,
               questions: response.toolResult?.args?.questions || '',
               intentType: 'positive',
               invitees: [
                  {
                     name:
                        `${leadDetails?.userInfo?.firstName} ${leadDetails?.userInfo?.lastName}` ||
                        '',
                     email: leadDetails?.userInfo?.email || '',
                  },
                  {
                     name: leadDetails?.agentInfo?.name || '',
                     email: leadDetails?.agentInfo?.email || '',
                  },
               ],
            }
         )

         if (currentLeadId) {
            handleConversationOutcome(appointmentItem, currentLeadId)
            updateTimelineSummary(
               currentLeadId,
               appointmentItem.timelineSummary || ''
            )
            updateQuestions(currentLeadId, appointmentItem.questions || '')

            updateAppointmentStatus(
               currentLeadId,
               response.toolResult?.args?.status || 'Scheduled',
               response.toolResult?.args?.date,
               response.toolResult?.args?.platform || 'Other'
            )
         }
         return null
      }
      return smsItem
   }
   const combinedMessages = useMemo(() => {
      const leadId = selectedLeadDetails?.userInfo.id
      if (!leadId) return combinedTimelineHistory || []

      const currentLeadOptimisticMessages = optimisticMessages[leadId] || []
      const result = [
         ...(combinedTimelineHistory || []),
         ...currentLeadOptimisticMessages,
      ]

      return result
   }, [
      combinedTimelineHistory,
      optimisticMessages,
      selectedLeadDetails?.userInfo.id,
   ])

   const recalculateAppointmentStatus = (
      conversationData: any[],
      leadId: number
   ) => {
      if (!leadId) return

      const lastAppointmentItem = findLastItem(
         conversationData,
         (item) => item.itemType === 'appointment'
      )

      if (lastAppointmentItem?.appointment) {
         updateAppointmentStatus(
            leadId,
            lastAppointmentItem.appointment.status || 'scheduled',
            lastAppointmentItem.appointment.date,
            lastAppointmentItem.appointment.platform
         )
      } else {
         updateAppointmentStatus(leadId, null, null, null)
      }
   }

   const recalculateLeadDetails = (conversationData: any[], leadId: number) => {
      if (!leadId) return

      const currentLeadDetails = getLeadDetails(leadId)
      if (!currentLeadDetails) return

      const initialValues = getInitialValues(leadId)

      const resetLeadDetails = {
         ...currentLeadDetails,
         conversationInfo: {
            messagesSent: 0,
            messagesReplied: 0,
            callsAttempted: 0,
            callsAnswered: 0,
         },
         appointmentInfo: {
            date: null,
            status: null,
            platform: null,
         },
         stageInfo: {
            id: 0,
            name: initialValues.phase || '',
         },
         timelineSummary: 'This lead has not started any conversation yet',
         questions: 'No questions have been asked by this lead yet',
      }

      setLeadDetails(leadId, resetLeadDetails)
      conversationData.forEach((item) =>
         handleConversationOutcome(item, leadId)
      )

      const updateCurrentStage = (stageName: string) => {
         updateStage(leadId, Number(resetLeadDetails.stageInfo.id), stageName)
         const { setCurrentCampaign } = useDemoConversationStore.getState()
         setCurrentCampaign(leadId, stageName)
      }

      if (conversationData.length > 0) {
         const lastStageItem = findLastItem(
            conversationData,
            (item) => item.campaignPhase
         )
         const newStageName =
            lastStageItem?.campaignPhase || selectedLeadDetails?.stageInfo?.name
         updateCurrentStage(newStageName)

         const lastTimelineSummaryItem = findLastItem(
            conversationData,
            (item) => item.timelineSummary
         )
         if (lastTimelineSummaryItem?.timelineSummary) {
            updateTimelineSummary(
               leadId,
               lastTimelineSummaryItem.timelineSummary
            )
         }

         const lastQuestionsItem = findLastItem(
            conversationData,
            (item) => item.questions
         )
         if (lastQuestionsItem?.questions) {
            updateQuestions(leadId, lastQuestionsItem.questions)
         }

         recalculateAppointmentStatus(conversationData, leadId)
      } else {
         updateCurrentStage(
            initialValues.phase || selectedLeadDetails?.stageInfo?.name || ''
         )
         updateTimelineSummary(
            leadId,
            resetLeadDetails.timelineSummary ||
               selectedLeadDetails?.leadSummary ||
               ''
         )
         updateQuestions(
            leadId,
            resetLeadDetails.questions || selectedLeadDetails?.questions || ''
         )
         updateAppointmentStatus(leadId, null, null, null)
      }
   }

   const handleSaveTimeline = async () => {
      if (!currentLeadId || isSavingLocal) return
      setIsSavingLocal(true)

      try {
         const success = await saveTimelineHistory(currentLeadId)
         if (success) {
            message.success('Timeline saved successfully!')
         } else {
            message.error('Failed to save timeline. Please try again.')
         }
      } catch (error) {
         console.error('Save timeline error:', error)
         message.error('An error occurred while saving.')
      } finally {
         setIsSavingLocal(false)
      }
   }

   const calculateItemsToRemove = (timeline: any[]) => {
      const lastItem = timeline[timeline.length - 1]
      let itemsToRemove = 1

      if (
         lastItem.itemType === 'post_analysis' ||
         lastItem.itemType === 'appointment'
      ) {
         const secondLastItem = timeline[timeline.length - 2]
         if (secondLastItem && secondLastItem.itemType === 'call') {
            itemsToRemove = 2
         }
      } else if (lastItem.itemType === 'data_validation') {
         const secondLastItem = timeline[timeline.length - 2]
         if (secondLastItem && secondLastItem.itemType === 'init_summary') {
            itemsToRemove = 2
         }
      }

      return itemsToRemove
   }

   useEffect(() => {
      if (selectedLeadDetails && currentLeadId) {
         const existingData = useDemoConversationStore
            .getState()
            .getLeadConversationData(currentLeadId)

         if (
            !existingData?.timelineHistory?.length &&
            selectedLeadDetails.preDefinedTimelineHistory
         ) {
            initializeTimelineHistory(
               currentLeadId,
               selectedLeadDetails.preDefinedTimelineHistory
            )

            const firstTimelineItem =
               selectedLeadDetails.preDefinedTimelineHistory[0]
            if (firstTimelineItem) {
               const initialPhase = firstTimelineItem.campaignPhase
               const initialSummary =
                  firstTimelineItem.timelineSummary ||
                  'This lead has not started any conversation yet'
               const initialQuestions =
                  firstTimelineItem.questions ||
                  'No questions have been asked by this lead yet'

               setInitialValues(
                  currentLeadId,
                  initialPhase,
                  initialSummary,
                  initialQuestions
               )
            }
         }

         const { setCurrentCampaignType, setCurrentCampaign } =
            useDemoConversationStore.getState()
         setCurrentCampaignType(
            currentLeadId,
            selectedLeadDetails?.leadInterestType || ''
         )

         if (existingData && existingData.timelineHistory?.length > 0) {
            const lastStageItem = findLastItem(
               existingData.timelineHistory,
               (item) => item.campaignPhase
            )
            const correctCampaignPhase =
               lastStageItem?.campaignPhase ||
               selectedLeadDetails?.stageInfo?.name
            setCurrentCampaign(currentLeadId, correctCampaignPhase)
         } else {
            setCurrentCampaign(
               currentLeadId,
               selectedLeadDetails?.stageInfo?.name
            )

            if (!selectedLeadDetails.preDefinedTimelineHistory?.length) {
               const initialPhase = selectedLeadDetails?.stageInfo?.name
               const initialSummary =
                  selectedLeadDetails?.leadSummary ||
                  'This lead has not started any conversation yet'
               const initialQuestions =
                  selectedLeadDetails?.questions ||
                  'No questions have been asked by this lead yet'

               setInitialValues(
                  currentLeadId,
                  initialPhase,
                  initialSummary,
                  initialQuestions
               )
            }
         }
      }
   }, [selectedLeadDetails, currentLeadId, initializeTimelineHistory])

   useEffect(() => {
      const processCallReport = async () => {
         if (
            endOfCallReport &&
            endOfCallReport.report &&
            endOfCallReport.report.type === 'end-of-call-report'
         ) {
            const { leadId: reportLeadId, report } = endOfCallReport

            try {
               const callData = await mapCallReportToTimelineItem(report)
               let analysisResponse = null

               if (report.endedReason === 'customer-ended-call') {
                  const updatedReport = {
                     ...report,
                     toolCallsExecuted: (
                        report.toolCallsExecuted as string[]
                     ).filter((tool: string) => tool !== 'set_appointment'),
                  }
                  analysisResponse =
                     await DemoConversationService.postDemoCallAnalysis(
                        updatedReport
                     )
               }

               const callItem = TimelineObjectFactory.createCallOrVoicemail({
                  content: callData.data,
                  data: callData.data,
                  itemType: callData.itemType,
                  itemActionStatus: callData.itemActionStatus,
                  callDuration: callData.duration,
                  phoneProviderCallId: callData.callId,
                  isIncoming: callData.isIncoming,
                  recordId: callData.recordId,
                  callEvents: callData.callEvents,
                  conversation: callData.conversation,
                  recordingUrl: callData.recordingUrl?.url,
                  recordingFileObjKey: callData.recordingFileObjKey,
                  timelineSummary: analysisResponse?.analysis?.timelineSummary,
                  questions: analysisResponse?.analysis?.questions,
                  intentType: analysisResponse?.analysis?.intentType,
                  ...getCampaignInfo(reportLeadId, currentStepIndex),
               })

               const updatedTimeline = [...currentTimeline, callItem]
               updateTimelineState(reportLeadId, updatedTimeline, callItem)
               handleConversationOutcome(callItem, reportLeadId)

               if (
                  analysisResponse?.analysis?.isAppointmentConfirmed === true ||
                  analysisResponse?.analysis?.stage === 'ff-appointment-set'
               ) {
                  const setAppointmentCall =
                     analysisResponse?.functionsCalls?.find(
                        (call: any) => call.name === 'set_appointment'
                     )
                  const appointmentDate =
                     setAppointmentCall?.arguments?.date || null
                  const appointmentStatus =
                     setAppointmentCall?.arguments?.status || 'Scheduled'
                  const appointmentPlatform =
                     setAppointmentCall?.arguments?.platform || 'Other'
                  const isCurrentlyInActiveConversation = reportLeadId
                     ? isActiveConversation(reportLeadId)
                     : false

                  const appointmentItem = await createStageChangeTimeline(
                     reportLeadId,
                     analysisResponse.analysis?.stage,
                     'appointment',
                     {
                        content: analysisResponse?.analysis?.callSummary,
                        appointment: {
                           date: appointmentDate,
                           status: appointmentStatus,
                           platform: appointmentPlatform,
                        },
                        timelineSummary:
                           analysisResponse?.analysis?.timelineSummary,
                        questions: analysisResponse?.analysis?.questions,
                        intentType: analysisResponse?.analysis?.intentType,
                        isActiveConversationItem:
                           isCurrentlyInActiveConversation,
                        invitees: [
                           {
                              name:
                                 `${analysisResponse?.leadDetails?.userInfo?.firstName} ${analysisResponse?.leadDetails?.userInfo?.lastName}` ||
                                 '',
                              email:
                                 analysisResponse?.leadDetails?.userInfo
                                    ?.email || '',
                           },
                           {
                              name:
                                 analysisResponse?.leadDetails?.agentInfo
                                    ?.name || '',
                              email:
                                 analysisResponse?.leadDetails?.agentInfo
                                    ?.email || '',
                           },
                        ],
                     }
                  )

                  handleConversationOutcome(appointmentItem, reportLeadId)
                  updateTimelineSummary(
                     reportLeadId,
                     analysisResponse.analysis?.timelineSummary || ''
                  )
                  updateQuestions(
                     reportLeadId,
                     analysisResponse.analysis?.questions || ''
                  )
               } else if (
                  analysisResponse?.analysis?.stage &&
                  analysisResponse.analysis.stage !==
                     getLeadDetails(reportLeadId)?.stageInfo?.name
               ) {
                  const stageItem = await createStageChangeTimeline(
                     reportLeadId,
                     analysisResponse.analysis.stage,
                     'post_analysis',
                     {
                        timelineSummary:
                           analysisResponse?.analysis?.timelineSummary,
                        questions: analysisResponse?.analysis?.questions,
                        content: analysisResponse.analysis.stage,
                        intentType: analysisResponse?.analysis?.intentType,
                     }
                  )

                  handleConversationOutcome(stageItem, reportLeadId)
                  updateTimelineSummary(
                     reportLeadId,
                     analysisResponse.analysis.timelineSummary || ''
                  )
                  updateQuestions(
                     reportLeadId,
                     analysisResponse.analysis.questions || ''
                  )
               } else if (
                  analysisResponse?.analysis?.nextCampaign === 'delay-short'
               ) {
                  const noteItem = await createNoteTimeline(
                     reportLeadId,
                     `${
                        analysisResponse?.analysis?.actionType === 'sms'
                           ? 'Text message'
                           : 'Phone Call'
                     } 
                      requested after ${analysisResponse?.analysis?.timeout}`,
                     {
                        timelineSummary:
                           analysisResponse?.analysis?.timelineSummary,
                        questions: analysisResponse?.analysis?.questions,
                        intentType: analysisResponse?.analysis?.intentType,
                        delayShortAction:
                           analysisResponse?.analysis?.actionType,
                     }
                  )

                  handleConversationOutcome(noteItem, reportLeadId)

                  if (analysisResponse?.analysis?.timelineSummary) {
                     updateTimelineSummary(
                        reportLeadId,
                        analysisResponse.analysis.timelineSummary
                     )
                  }
                  if (analysisResponse?.analysis?.questions) {
                     updateQuestions(
                        reportLeadId,
                        analysisResponse.analysis.questions
                     )
                  }
               }

               if (analysisResponse?.analysis) {
                  updateTimelineSummary(
                     reportLeadId,
                     analysisResponse.analysis?.timelineSummary || ''
                  )
                  updateQuestions(
                     reportLeadId,
                     analysisResponse.analysis?.questions || ''
                  )
               }

               clearAllLoadingStates(reportLeadId)
            } catch (error) {
               console.error('Error processing call report:', error)
               clearAllLoadingStates(reportLeadId)
            }
         }
      }

      if (endOfCallReport) {
         processCallReport()
      }
   }, [endOfCallReport])

   const handleDelayShortAction = async (
      leadId: number,
      actionType?: string
   ) => {
      setLoadingStates(leadId, true)

      try {
         const leadDetails = getLeadDetails(leadId)
         if (!leadDetails) {
            clearAllLoadingStates(leadId)
            return
         }

         const activeConversationScript =
            await DemoConversationService.getActiveConversationScript(
               (currentCampaignType ?? '').toLowerCase(),
               currentCampaign ?? '',
               actionType === 'phone-call' ? 'outbound-call' : 'sms'
            )

         const voicemailPrompt = activeConversationScript?.voicemailPrompt || ''

         let newConversationItem = null

         if (actionType === 'phone-call') {
            setWaitingForCallReport(leadId, true)

            const response = await DemoConversationService.generateDemoCall(
               activeConversationScript?.script || '',
               voicemailPrompt,
               {
                  ...leadDetails,
                  preDefinedTimelineHistory: [...currentTimeline].reverse(),
               },
               progressDetails || {},
               true
            )

            if (leadId !== undefined) {
               startSSEListener(response.id, leadId)
            }

            return
         } else if (actionType === 'sms') {
            const response = await DemoConversationService.generateDemoSms(
               {
                  ...leadDetails,
                  preDefinedTimelineHistory: [...currentTimeline].reverse(),
               },
               [...currentTimeline].reverse(),
               activeConversationScript?.script || '',
               progressDetails || {}
            )

            const campaignInfo = {
               campaignType: getNextStepInfoMemo?.campaignType,
               campaignPhase: getNextStepInfoMemo?.campaignPhase,
               step: (getNextStepInfoMemo?.step ?? 1) - 1,
               stepIndex: currentStepIndex,
            }

            newConversationItem = await handleSMSWithAppointment(
               leadDetails,
               response,
               campaignInfo,
               leadId
            )
         }

         if (newConversationItem) {
            setNextConversationLoading(leadId, false)

            setTimeout(() => {
               if (leadId) {
                  const updated = [...currentTimeline, newConversationItem]
                  updateTimelineState(leadId, updated, newConversationItem)
                  handleConversationOutcome(newConversationItem, leadId)
               }
               setDemoLoading(leadId, false)
            }, 50)
         } else {
            setLoadingStates(leadId, false)
         }
      } catch (error) {
         console.error('Error executing delay-short action:', error)
         clearAllLoadingStates(leadId)
      }
   }

   useEffect(() => {
      const handleKeyPress = async (event: KeyboardEvent) => {
         if (isDemoLoading || isNextConversationLoading) return

         if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowRight') {
            event.preventDefault()

            if (currentLeadId === undefined) return

            setLoadingStates(currentLeadId, true)

            if (currentLeadId && isInActiveConversation) {
               endActiveConversation(currentLeadId)
            }

            setTimeout(async () => {
               let newConversationItem = null

               const hasNoTimeline = currentTimeline.length === 0
               const hasNoInitialComponents = !currentTimeline.some(
                  (item) =>
                     item.itemType === 'init_summary' ||
                     item.itemType === 'data_validation'
               )

               const predefinedHasInitialComponents =
                  selectedLeadDetails?.preDefinedTimelineHistory?.some(
                     (item) =>
                        item.itemType === 'init_summary' ||
                        item.itemType === 'data_validation'
                  )

               const hasNoPredefined =
                  !selectedLeadDetails?.preDefinedTimelineHistory ||
                  selectedLeadDetails.preDefinedTimelineHistory.length === 0
               const shouldCreateInitialComponents =
                  hasNoTimeline && hasNoInitialComponents

               let needsInitialSetup = false
               if (navigationMode === 'campaign') {
                  needsInitialSetup = shouldCreateInitialComponents
               } else if (navigationMode === 'predefined') {
                  needsInitialSetup =
                     shouldCreateInitialComponents &&
                     (!predefinedHasInitialComponents || hasNoPredefined)
               }

               if (needsInitialSetup) {
                  try {
                     const leadDetails = getLeadDetails(currentLeadId ?? 0)

                     if (leadDetails) {
                        const enhancedLeadDetails = {
                           ...leadDetails,
                           preDefinedTimelineHistory: [
                              ...currentTimeline,
                           ].reverse(),
                           nextCampaign: null,
                           nextStep: null,
                           nextTimeout: null,
                           nextAction: null,
                           isValidated: false,
                           lastExecutedCampaign: null,
                           lastExecutedStep: null,
                        }

                        const response =
                           await DemoConversationService.processDemoConversation(
                              'preview',
                              [],
                              enhancedLeadDetails
                           )

                        const initialSummaryItem =
                           TimelineObjectFactory.createInitialSummary({
                              content: response?.meta?.timelineSummary,
                              timelineSummary:
                                 response?.meta?.shortTimelineSummary,
                              questions: response?.meta?.questions,
                              campaignType: currentCampaignType,
                              campaignPhase: currentCampaign,
                              step: 0,
                              stepIndex: 0,
                           })

                        const itemsToAdd = [initialSummaryItem]

                        if (response?.eventType === 'lead-validation') {
                           const leadValidationItem =
                              TimelineObjectFactory.createLeadValidation({
                                 content:
                                    response?.meta?.validationMessage ||
                                    'Lead validation completed',
                                 timelineSummary:
                                    response?.meta?.shortTimelineSummary,
                                 questions: response?.meta?.questions,
                                 campaignType: currentCampaignType,
                                 campaignPhase: currentCampaign,
                                 step: 0,
                                 stepIndex: 1,
                              })

                           itemsToAdd.push(leadValidationItem)
                        }

                        if (currentLeadId) {
                           const updated = [...currentTimeline, ...itemsToAdd]
                           updateTimelineState(
                              currentLeadId,
                              updated,
                              itemsToAdd[itemsToAdd.length - 1]
                           )

                           itemsToAdd.forEach((item) => {
                              handleConversationOutcome(item, currentLeadId)
                           })
                        }

                        updateTimelineSummary(
                           currentLeadId ?? 0,
                           response?.meta?.shortTimelineSummary || ''
                        )
                        updateQuestions(
                           currentLeadId ?? 0,
                           response?.meta?.questions
                        )

                        clearAllLoadingStates(currentLeadId)
                        return
                     }
                  } catch (error) {
                     console.error('Error creating initial summary:', error)
                     clearAllLoadingStates(currentLeadId)
                     return
                  }
               } else {
                  if (navigationMode === 'campaign') {
                     const delayShortActionFromTimeline =
                        getDelayShortActionFromTimeline(currentTimeline)
                     if (delayShortActionFromTimeline) {
                        handleDelayShortAction(
                           currentLeadId,
                           delayShortActionFromTimeline
                        )
                        return
                     }

                     const nextStepInfo = getNextStepInfoMemo?.step ?? 1
                     const currentStep = campaignSteps.find(
                        (step) => step.step === `Step ${nextStepInfo}`
                     )

                     if (currentStep?.actionType === 'phone-call') {
                        setWaitingForCallReport(currentLeadId, true)

                        try {
                           const leadDetails = getLeadDetails(
                              currentLeadId ?? 0
                           )
                           if (leadDetails) {
                              const response =
                                 await DemoConversationService.generateDemoCall(
                                    currentStep.basePrompt || '',
                                    currentStep?.meta?.voicemailBasePrompt ||
                                       '',
                                    {
                                       ...leadDetails,
                                       preDefinedTimelineHistory: [
                                          ...currentTimeline,
                                       ].reverse(),
                                    },
                                    progressDetails || {},
                                    currentStep?.meta?.sendVoiceMail
                                 )

                              if (currentLeadId !== undefined) {
                                 startSSEListener(response.id, currentLeadId)
                              }
                              return
                           }
                        } catch (error) {
                           clearAllLoadingStates(currentLeadId)
                        }
                     } else if (currentStep?.actionType === 'sms') {
                        try {
                           const leadDetails = getLeadDetails(
                              currentLeadId ?? 0
                           )
                           if (leadDetails) {
                              const response =
                                 await DemoConversationService.generateDemoSms(
                                    {
                                       ...leadDetails,
                                       preDefinedTimelineHistory: [
                                          ...currentTimeline,
                                       ].reverse(),
                                    },
                                    [...currentTimeline].reverse(),
                                    currentStep.basePrompt || '',
                                    progressDetails || {}
                                 )

                              const campaignInfo = {
                                 campaignType:
                                    getNextStepInfoMemo?.campaignType,
                                 campaignPhase:
                                    getNextStepInfoMemo?.campaignPhase,
                                 step: getNextStepInfoMemo?.step,
                                 stepIndex: currentStepIndex,
                              }

                              newConversationItem =
                                 await handleSMSWithAppointment(
                                    leadDetails,
                                    response,
                                    campaignInfo,
                                    currentLeadId
                                 )
                           }
                        } catch (error) {
                           console.error('Error sending SMS:', error)
                        }
                     }
                  } else if (navigationMode === 'predefined') {
                     if (currentStepIndex < preDefinedTimelineHistory.length) {
                        newConversationItem = {
                           ...preDefinedTimelineHistory[currentStepIndex],
                        }
                     } else {
                        const delayShortActionFromTimeline =
                           getDelayShortActionFromTimeline(currentTimeline)
                        if (delayShortActionFromTimeline) {
                           handleDelayShortAction(
                              currentLeadId,
                              delayShortActionFromTimeline
                           )
                           return
                        }

                        const currentStepNumber = getNextStepInfoMemo?.step ?? 1
                        const currentStep = campaignSteps.find(
                           (step) => step.step === `Step ${currentStepNumber}`
                        )

                        if (currentStep?.actionType === 'phone-call') {
                           try {
                              const leadDetails = getLeadDetails(
                                 currentLeadId ?? 0
                              )
                              if (leadDetails) {
                                 const response =
                                    await DemoConversationService.generateDemoCall(
                                       currentStep.basePrompt || '',
                                       currentStep?.meta?.voicemailBasePrompt ||
                                          '',
                                       {
                                          ...leadDetails,
                                          preDefinedTimelineHistory: [
                                             ...currentTimeline,
                                          ].reverse(),
                                       },
                                       progressDetails || {},
                                       currentStep?.meta?.sendVoiceMail
                                    )
                                 if (currentLeadId !== undefined) {
                                    startSSEListener(response.id, currentLeadId)
                                 }
                                 return
                              }
                           } catch (error) {
                              clearAllLoadingStates(currentLeadId)
                           }
                        } else if (currentStep?.actionType === 'sms') {
                           try {
                              const leadDetails = getLeadDetails(
                                 currentLeadId ?? 0
                              )
                              if (leadDetails) {
                                 const response =
                                    await DemoConversationService.generateDemoSms(
                                       {
                                          ...leadDetails,
                                          preDefinedTimelineHistory: [
                                             ...currentTimeline,
                                          ].reverse(),
                                       },
                                       [...currentTimeline].reverse(),
                                       currentStep.basePrompt || '',
                                       progressDetails || {}
                                    )

                                 const campaignInfo = {
                                    campaignType:
                                       getNextStepInfoMemo?.campaignType,
                                    campaignPhase:
                                       getNextStepInfoMemo?.campaignPhase,
                                    step: getNextStepInfoMemo?.step,
                                    stepIndex: currentStepIndex,
                                 }

                                 newConversationItem =
                                    await handleSMSWithAppointment(
                                       leadDetails,
                                       response,
                                       campaignInfo,
                                       currentLeadId
                                    )
                              }
                           } catch (error) {
                              console.error('Error sending SMS:', error)
                           }
                        }
                     }
                  }
               }

               if (newConversationItem) {
                  setNextConversationLoading(currentLeadId ?? 0, false)

                  setTimeout(() => {
                     if (currentLeadId) {
                        const updated = [
                           ...currentTimeline,
                           newConversationItem,
                        ]
                        updateTimelineState(
                           currentLeadId,
                           updated,
                           newConversationItem
                        )
                        handleConversationOutcome(
                           newConversationItem,
                           currentLeadId
                        )
                     }

                     setDemoLoading(currentLeadId, false)
                  }, 50)
               } else {
                  setLoadingStates(currentLeadId, false)
               }
            }, 1000)
         } else if (
            (event.ctrlKey || event.metaKey) &&
            event.key === 'ArrowLeft'
         ) {
            event.preventDefault()

            if (currentLeadId && currentTimeline.length > 0) {
               const itemsToRemove = calculateItemsToRemove(currentTimeline)
               const updated = currentTimeline.slice(0, -itemsToRemove)

               updateTimelineState(
                  currentLeadId,
                  updated,
                  updated[updated.length - 1]
               )
               recalculateLeadDetails(updated, currentLeadId)

               if (updated.length === 0) {
                  const initialValues = getInitialValues(currentLeadId)
                  const { setCurrentCampaign } =
                     useDemoConversationStore.getState()

                  if (initialValues.phase) {
                     setCurrentCampaign(currentLeadId, initialValues.phase)
                  }
               }
            }
         } else if (
            (event.ctrlKey || event.metaKey) &&
            event.key === 'Backspace'
         ) {
            event.preventDefault()
            setNextConversationLoading(currentLeadId ?? 0, false)

            if (currentLeadId && isInActiveConversation) {
               endActiveConversation(currentLeadId)
            }

            if (currentLeadId) {
               updateTimelineState(currentLeadId, [], null)
               recalculateLeadDetails([], currentLeadId)
            }

            message.success('Conversation reset')
         }
      }

      document.addEventListener('keydown', handleKeyPress)
      return () => document.removeEventListener('keydown', handleKeyPress)
   }, [
      currentStepIndex,
      campaignSteps,
      updateTimelineHistory,
      setLastUpdate,
      isDemoLoading,
      isNextConversationLoading,
      currentLeadId,
      handleConversationOutcome,
      navigationMode,
      preDefinedTimelineHistory,
      canSaveTimeline,
      isWaitingForReport,
      isInActiveConversation,
      activeConversationInfo,
      currentTimeline,
      getNextStepInfoMemo,
      getInitialValues,
   ])

   useEffect(() => {
      if (scrollRef.current) {
         scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
   }, [combinedMessages, isNextConversationLoading])

   const processOptimisticMessage = async (
      leadId: number,
      leadDetails: any,
      currentTimelineWithLeadMessage: any[] = []
   ) => {
      try {
         const stepToBeExecuted = getNextStepInfoMemo?.step ?? 1

         const enhancedLeadDetails = {
            ...leadDetails,
            preDefinedTimelineHistory: [
               ...currentTimelineWithLeadMessage,
            ].reverse(),
            nextCampaign: getNextStep(
               currentCampaignType ?? '',
               currentCampaign ?? '',
               stepToBeExecuted - 1
            )?.nextCampaign,
            nextStep: getNextStep(
               currentCampaignType ?? '',
               currentCampaign ?? '',
               stepToBeExecuted - 1
            )?.nextStep,
            nextTimeout: null,
            nextAction: getNextStep(
               currentCampaignType ?? '',
               currentCampaign ?? '',
               stepToBeExecuted - 1
            )?.nextAction,
            isValidated: true,
            lastCampaign: 'Active Conversation',
            lastExecutedCampaign: currentCampaign,
            lastExecutedStep: stepToBeExecuted - 1,
         }

         const stepResponse =
            await DemoConversationService.processDemoConversation(
               null,
               [...currentTimelineWithLeadMessage].reverse() || [],
               enhancedLeadDetails
            )

         const responseData = stepResponse?.data || stepResponse
         let campaignPhase, step, actionType, campaignType, stage

         if (responseData) {
            campaignPhase = responseData.campaign || responseData.campaignPhase
            step = responseData.step
            actionType = responseData.action || responseData.actionType
            campaignType = responseData.campaignType || currentCampaignType
            stage = responseData.stage
         }

         const currentLeadStage = getLeadDetails(leadId)?.stageInfo?.name

         if (
            stage &&
            stage !== currentLeadStage &&
            (!actionType || actionType === null || actionType === '')
         ) {
            const metaData =
               responseData?.meta?.meta || responseData?.meta || {}

            const stageItem = await createStageChangeTimeline(
               leadId,
               stage,
               'post_analysis',
               {
                  timelineSummary:
                     metaData?.timelineSummary ||
                     responseData?.timelineSummaryAnalysis,
                  questions: metaData?.questions,
                  content: stage,
                  intentType: metaData?.intentType,
                  isActiveConversationItem: true,
               }
            )

            const finalUpdatedTimeline = [
               ...currentTimelineWithLeadMessage,
               stageItem,
            ]
            updateTimelineState(leadId, finalUpdatedTimeline, stageItem)
            handleConversationOutcome(stageItem, leadId)

            if (metaData?.timelineSummary) {
               updateTimelineSummary(leadId, metaData.timelineSummary)
            }
            if (metaData?.questions) {
               updateQuestions(leadId, metaData.questions)
            }

            setOptimisticMessages((prev) => ({
               ...prev,
               [leadId]: [],
            }))
            setNextConversationLoading(leadId, false)
            return
         }

         if (!actionType || actionType === null || actionType === '') {
            setOptimisticMessages((prev) => ({
               ...prev,
               [leadId]: [],
            }))
            setNextConversationLoading(leadId, false)
            return
         }

         const activeConversationScript =
            await DemoConversationService.getActiveConversationScript(
               (currentCampaignType ?? '').toLowerCase(),
               currentCampaign ?? '',
               actionType === 'phone-call' ? 'outbound-call' : 'sms'
            )

         let finalResponse
         switch (actionType) {
            case 'sms':
               finalResponse = await DemoConversationService.generateDemoSms(
                  leadDetails,
                  [...currentTimelineWithLeadMessage].reverse(),
                  activeConversationScript?.script || '',
                  progressDetails || {}
               )
               break

            case 'phone-call': {
               setWaitingForCallReport(leadId, true)

               const voicemailPrompt =
                  activeConversationScript?.voicemailPrompt || ''

               finalResponse = await DemoConversationService.generateDemoCall(
                  activeConversationScript?.script || '',
                  voicemailPrompt,
                  leadDetails,
                  progressDetails || {},
                  true
               )

               if (finalResponse?.id) {
                  startSSEListener(finalResponse.id, leadId)
                  return
               }
               break
            }
            default:
               throw new Error(`Unsupported action type: ${actionType}`)
         }

         const responseStepIndex = currentTimelineWithLeadMessage.length
         const isCurrentlyInActiveConversation = currentLeadId
            ? isActiveConversation(currentLeadId)
            : false
         const currentActiveConversationInfo = currentLeadId
            ? getActiveConversationInfo(currentLeadId)
            : null

         const campaignInfo =
            isCurrentlyInActiveConversation && currentActiveConversationInfo
               ? {
                    campaignType:
                       currentActiveConversationInfo.lastCampaignType,
                    campaignPhase:
                       currentActiveConversationInfo.lastCampaignPhase,
                    step: currentActiveConversationInfo.lastStep,
                    stepIndex: responseStepIndex,
                    isActiveConversationItem: true,
                 }
               : {
                    campaignType: campaignType,
                    campaignPhase: campaignPhase,
                    step: step,
                    stepIndex: responseStepIndex,
                    isActiveConversationItem: false,
                 }

         let timelineItem = null

         if (
            finalResponse?.toolResult?.toolName === 'set_delay' &&
            actionType === 'sms'
         ) {
            const delayActionType = finalResponse?.toolResult?.args?.actionType
            const timeout = finalResponse?.toolResult?.args?.timeout
            const noteItem = await createNoteTimeline(
               leadId,
               `${
                  delayActionType === 'sms' ? 'Text message' : 'Phone Call'
               } requested after ${timeout}`,
               {
                  delayShortAction: delayActionType,
                  isActiveConversationItem: true,
               }
            )

            const finalUpdatedTimeline = [
               ...currentTimelineWithLeadMessage,
               noteItem,
            ]
            updateTimelineState(leadId, finalUpdatedTimeline, noteItem)

            setOptimisticMessages((prev) => ({
               ...prev,
               [leadId]: [],
            }))
            setNextConversationLoading(leadId, false)
            return
         }

         if (actionType === 'sms') {
            if (
               finalResponse.toolResult?.toolName === 'set_appointment' &&
               finalResponse.toolResult?.args
            ) {
               const smsItem = TimelineObjectFactory.createSMS({
                  content: finalResponse.content,
                  personId: leadId,
                  ...campaignInfo,
               })

               const timelineWithSMS = [
                  ...currentTimelineWithLeadMessage,
                  smsItem,
               ]
               updateTimelineState(leadId, timelineWithSMS, smsItem)
               handleConversationOutcome(smsItem, leadId)

               const currentLeadDetails = getLeadDetails(leadId)

               if (currentLeadDetails) {
                  const appointmentItem = await createStageChangeTimeline(
                     leadId,
                     'ff-appointment-set',
                     'appointment',
                     {
                        content: finalResponse.content || '',
                        appointment: {
                           date: finalResponse.toolResult.args.date,
                           status:
                              finalResponse.toolResult.args.status ||
                              'Scheduled',
                           platform:
                              finalResponse.toolResult.args.platform || 'Other',
                        },
                        timelineSummary:
                           finalResponse.toolResult.args.timelineSummary,
                        questions: finalResponse.toolResult.args.questions,
                        intentType: 'positive',
                        invitees: [
                           {
                              name:
                                 `${currentLeadDetails?.userInfo?.firstName} ${currentLeadDetails?.userInfo?.lastName}` ||
                                 '',
                              email: currentLeadDetails?.userInfo?.email || '',
                           },
                           {
                              name: currentLeadDetails?.agentInfo?.name || '',
                              email: currentLeadDetails?.agentInfo?.email || '',
                           },
                        ],
                        isActiveConversationItem: true,
                     }
                  )

                  handleConversationOutcome(appointmentItem, leadId)
                  updateTimelineSummary(
                     leadId,
                     appointmentItem.timelineSummary || ''
                  )
                  updateQuestions(leadId, appointmentItem.questions || '')

                  updateAppointmentStatus(
                     leadId,
                     finalResponse.toolResult.args.status || 'Scheduled',
                     finalResponse.toolResult.args.date,
                     finalResponse.toolResult.args.platform || 'Other'
                  )
               }

               setOptimisticMessages((prev) => ({
                  ...prev,
                  [leadId]: [],
               }))
               setNextConversationLoading(leadId, false)
               return
            } else {
               timelineItem = TimelineObjectFactory.createSMS({
                  content: finalResponse.content,
                  personId: leadId,
                  ...campaignInfo,
               })
            }
         }

         if (timelineItem) {
            const finalUpdatedTimeline = [
               ...currentTimelineWithLeadMessage,
               timelineItem,
            ]
            updateTimelineState(leadId, finalUpdatedTimeline, timelineItem)
            handleConversationOutcome(timelineItem, leadId)
         }

         setOptimisticMessages((prev) => ({
            ...prev,
            [leadId]: [],
         }))
         setNextConversationLoading(leadId, false)
      } catch (error) {
         console.error('Error processing optimistic message:', error)
         setOptimisticMessages((prev) => ({
            ...prev,
            [leadId]: [],
         }))
         setNextConversationLoading(leadId, false)
         message.error('Failed to process message')
      }
   }

   const addOptimisticMessage = async (
      messageContent: string,
      isBuyer: boolean
   ) => {
      if (!currentLeadId) return

      const wasNotInActiveConversation = !isInActiveConversation

      if (wasNotInActiveConversation) {
         startActiveConversation(
            currentLeadId,
            currentCampaignType || '',
            currentCampaign || '',
            (getNextStepInfoMemo?.step ?? 1) - 1,
            currentTimeline.length
         )
      }

      const campaignInfo = wasNotInActiveConversation
         ? {
              campaignType: currentCampaignType || '',
              campaignPhase: currentCampaign || '',
              step: (getNextStepInfoMemo?.step ?? 1) - 1,
              stepIndex: currentStepIndex,
              isActiveConversationItem: true,
           }
         : getCampaignInfo(currentLeadId, currentStepIndex)

      const optimisticUserMessage = TimelineObjectFactory.createIncomingSMS({
         content: messageContent,
         personId: currentLeadId,
         assistant: {
            avatar: selectedLeadDetails?.userInfo.avatar || null,
            name: selectedLeadDetails?.userInfo.firstName || 'Lead',
         },
         ...campaignInfo,
      })

      const updatedWithUser = [...currentTimeline, optimisticUserMessage]

      updateTimelineState(currentLeadId, updatedWithUser, optimisticUserMessage)
      handleConversationOutcome(optimisticUserMessage, currentLeadId)

      if (isBuyer) {
         setNextConversationLoading(currentLeadId, true)

         const leadDetails = getLeadDetails(currentLeadId)
         if (leadDetails) {
            const enhancedLeadDetails = {
               ...leadDetails,
               preDefinedTimelineHistory: [...updatedWithUser].reverse(),
            }

            await processOptimisticMessage(
               currentLeadId,
               enhancedLeadDetails,
               updatedWithUser
            )
         }
      }
   }

   return (
      <div className="flex flex-col h-full">
         <div className="flex justify-between px-8 py-4">
            <p className="text-[14px] font-medium">Conversation History</p>
            {(user?.email === 'operator@forward-flow.com' ||
               user?.email === 'brannon@forward-flow.com' ||
               user?.email === 'engineered@forward-flow.com') &&
               showOperatorSection && (
                  <div className="flex items-center gap-4">
                     {
                        <div className="flex items-center gap-2">
                           <Button
                              onClick={handleSaveTimeline}
                              isDisabled={!canSaveTimeline || isSavingLocal}
                              className="!w-auto !h-[32px] !px-3 !py-1 !mt-0 !rounded-lg border border-primary/30 bg-primary/10 hover:!bg-primary/20 transition-colors text-white/80 hover:text-white"
                           >
                              {isSavingLocal ? (
                                 <div className="flex items-center gap-2">
                                    <div className="animate-spin">
                                       <Icon
                                          name="loader"
                                          className="fill-white/70"
                                          width={14}
                                          height={14}
                                       />
                                    </div>
                                    <span className="text-[12px]">
                                       Saving...
                                    </span>
                                 </div>
                              ) : (
                                 <div className="flex items-center gap-2">
                                    <Icon
                                       name="save"
                                       className="fill-white/70"
                                       width={14}
                                       height={14}
                                    />
                                    <span className="text-[12px]">
                                       Save Timeline
                                    </span>
                                 </div>
                              )}
                           </Button>
                        </div>
                     }

                     <div className="flex items-center gap-2">
                        <span className="text-[12px] text-white/60">
                           Navigation Mode:
                        </span>
                        <button
                           onClick={() =>
                              setNavigationMode((prev) =>
                                 prev === 'campaign' ? 'predefined' : 'campaign'
                              )
                           }
                           className={`px-3 py-1 text-[12px] rounded-lg border transition-colors ${
                              navigationMode === 'campaign'
                                 ? 'bg-primary/20 border-primary/30 text-white'
                                 : 'bg-white/10 border-white/20 text-white/70'
                           }`}
                        >
                           {navigationMode === 'campaign'
                              ? 'Dynamic'
                              : 'Predefined'}
                        </button>
                        {navigationMode === 'predefined' &&
                           currentStepIndex >=
                              preDefinedTimelineHistory.length && (
                              <span className="text-[10px] text-yellow-400">
                                 → Campaign fallback
                              </span>
                           )}
                     </div>
                  </div>
               )}
         </div>
         <div
            data-test-id="conversation-history-scrollable-container"
            id="scrollableDiv"
            ref={scrollRef}
            className="relative flex flex-col flex-grow px-2 overflow-y-auto gap-y-8"
         >
            <div className="flex flex-col px-2 gap-y-6">
               {combinedMessages.length > 0 &&
                  combinedMessages.map((item: any, index: any) => {
                     const key = `${item.id}-${item.itemType}-${item.ts}-${index}`

                     const TimelineItemWrapper: FC<{
                        children: React.ReactNode
                     }> = ({ children }) => (
                        <CtrlClickSaveWrapper
                           leadId={currentLeadId!}
                           stepIndex={index}
                           navigationMode={navigationMode}
                           className="rounded-lg"
                        >
                           {children}
                        </CtrlClickSaveWrapper>
                     )

                     if (
                        item.itemType === 'stage' ||
                        item.itemType === 'data_validation' ||
                        item.itemType === 'post_analysis'
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <StageChangeItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (item?.itemType === 'note') {
                        return (
                           <TimelineItemWrapper key={key}>
                              <NotesItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (item?.itemType === 'event') {
                        return (
                           <TimelineItemWrapper key={key}>
                              <EventItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (item.itemType === 'text' && !item.isIncoming) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <AgentMessageItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (item.itemType === 'text' && item.isIncoming) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <LeadMessageItem
                                 item={item}
                                 selectedLeadDetails={selectedLeadDetails}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item?.itemType === 'call' &&
                        item?.content &&
                        !item.isIncoming &&
                        item.callEvents === null
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <CallLeadItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item?.itemType === 'call' &&
                        item?.itemActionStatus === 'missed'
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <VoiceMissedItem
                                 item={item}
                                 selectedLeadDetails={selectedLeadDetails}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item?.itemType === 'voicemail' &&
                        !item.isIncoming &&
                        (item?.itemActionStatus === 'voicemail_missed' ||
                           item?.itemActionStatus === 'voicemail_sent')
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <VoiceMissedItem
                                 item={item}
                                 selectedLeadDetails={selectedLeadDetails}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item.itemType === 'call' &&
                        !item.isIncoming &&
                        item.callEvents
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <CallSummaryAgentItem
                                 item={item}
                                 selectedLeadDetails={selectedLeadDetails}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item?.itemType === 'call' &&
                        item?.isIncoming &&
                        item?.callEvents
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <CallSummaryLeadItem
                                 item={item}
                                 selectedLeadDetails={selectedLeadDetails}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (item?.itemType === 'call' && item.isIncoming) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <CallAgentItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item?.itemType === 'email' &&
                        !item.isIncoming
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <EmailSentItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item.itemType === 'appointment' &&
                        item.appointment?.status
                           ?.toLowerCase()
                           .includes('scheduled')
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <AppointmentScheduledItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (item.itemType === 'appointment_missed') {
                        return (
                           <TimelineItemWrapper key={key}>
                              <AppointmentMissedItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item.itemType === 'appointment' &&
                        item.appointment.status === 'rescheduled'
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <AppointmentRescheduledItem
                                 item={item}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else if (
                        item.itemType === 'init_summary' &&
                        !item.isIncoming
                     ) {
                        return (
                           <TimelineItemWrapper key={key}>
                              <InitialLeadSummaryItem
                                 item={item}
                                 leadDetails={selectedLeadDetails}
                                 data-message-id={item.id}
                              />
                           </TimelineItemWrapper>
                        )
                     } else {
                        return null
                     }
                  })}

               {isNextConversationLoading && <AgentLoaderItem />}
            </div>
         </div>

         <div className="flex items-center w-full pl-2 my-2">
            <MessageInput
               leadId={selectedLeadDetails?.userInfo.id ?? null}
               onOptimisticUpdate={addOptimisticMessage}
            />
         </div>
      </div>
   )
}

export default DemoConversation
